import { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import {
  Box,
  Text,
  Badge,
  HStack,
  Stack,
  Avatar,
  Input,
  createListCollection,
} from '@chakra-ui/react';
import { Separator } from '@/components/ui/separator';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { FaMapMarkerAlt, FaSync } from 'react-icons/fa';
import {
  useUpdateLocation,
  useFetchLocationInfo,
  locationsQueryKeys,
} from '../locations.hook';
import { Location, UpdateLocationBody, AuthType } from '../locations.types';
import { toaster } from '@/components/ui/toaster';
import { Select } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { defaultApiClient } from '@/services/api-client';

// Language collection
const languageCollection = createListCollection({
  items: [
    { value: '', label: 'Select Language (Optional)', code: '' },
    { value: 'en', label: 'English', code: 'en' },
    { value: 'nl', label: 'Dutch', code: 'nl' },
    { value: 'de', label: 'German', code: 'de' },
    { value: 'fr', label: 'French', code: 'fr' },
    { value: 'es', label: 'Spanish', code: 'es' },
    { value: 'it', label: 'Italian', code: 'it' },
    { value: 'pt', label: 'Portuguese', code: 'pt' },
    { value: 'ru', label: 'Russian', code: 'ru' },
    { value: 'zh', label: 'Chinese', code: 'zh' },
    { value: 'ja', label: 'Japanese', code: 'ja' },
  ],
  itemToString: (item) => item.label,
  itemToValue: (item) => item.value,
});

const locationSchema = z.object({
  name: z.string().min(1, 'Location name is required'),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  postal_code: z.string().optional(),
  country_code: z.string().optional(),
  language_code: z.string().optional(),
  url: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        // Allow URLs with or without protocol
        try {
          // Try with protocol first
          new URL(val);
          return true;
        } catch {
          // Try adding https:// if no protocol
          try {
            new URL(`https://${val}`);
            return true;
          } catch {
            return false;
          }
        }
      },
      { message: 'Invalid URL format' },
    )
    .or(z.literal('')),
  is_active: z.boolean(),
  // Agent configuration fields are read-only (managed via webhook/sync)
});

type LocationFormData = z.infer<typeof locationSchema>;

interface LocationEditFormProps {
  location: Location;
  onSuccess?: () => void;
  isViewMode?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

export interface LocationEditFormRef {
  submit: () => void;
  reset: () => void;
}

export const LocationEditForm = forwardRef<
  LocationEditFormRef,
  LocationEditFormProps
>(({ location, onSuccess, isViewMode = false, onSubmittingChange }, ref) => {
  const queryClient = useQueryClient();
  const updateLocationMutation = useUpdateLocation();
  const [isSyncing, setIsSyncing] = useState(false);

  // Hooks for syncing agent settings
  const fetchLocationInfoMutation = useFetchLocationInfo();

  const handleSyncAgentSettings = async () => {
    setIsSyncing(true);
    try {
      // Check if location has an auth_id configured
      if (!location.auth_id) {
        toaster.create({
          title: 'No Auth Credentials',
          description:
            'No authentication credentials configured for this location. Please set auth_id first.',
          type: 'warning',
        });
        return;
      }

      // Fetch the auth type using the location's auth_id
      const response = await defaultApiClient.get<{ data: AuthType }>(
        `/api/v1/auth-types/${location.auth_id}`,
      );

      const authType = response.data.data;

      if (!authType) {
        toaster.create({
          title: 'Auth Not Found',
          description: 'The configured auth credentials could not be found.',
          type: 'warning',
        });
        return;
      }

      // Use the auth type to fetch location info and update the location
      await fetchLocationInfoMutation.mutateAsync({
        authType,
        locationId: location.id,
      });

      // Invalidate location query to refresh the data
      await queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.location(location.id),
      });

      toaster.create({
        title: 'Settings Synced',
        description:
          'Agent configuration has been synced from ClubPlanner successfully.',
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to sync agent settings:', error);
      toaster.create({
        title: 'Sync Failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to sync agent settings.',
        type: 'error',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    control,
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      name: location.name || '',
      address: location.address || '',
      phone: location.phone || '',
      email: location.email || '',
      postal_code: location.postal_code || '',
      country_code: location.country_code || '',
      language_code: location.language_code || '',
      url: location.url || '',
      is_active: location.is_active ?? true,
      // Agent configuration fields are read-only (managed via webhook/sync)
    },
  });

  // Expose submit and reset methods via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(onSubmit)();
    },
    reset: () => {
      reset();
    },
  }));

  // Update parent component when submitting state changes
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const onSubmit = async (data: LocationFormData) => {
    try {
      const updateData: UpdateLocationBody = {
        name: data.name,
        address: data.address || undefined,
        phone: data.phone || undefined,
        email: data.email || undefined,
        postal_code: data.postal_code || undefined,
        country_code: data.country_code || undefined,
        language_code: data.language_code || undefined,
        url: data.url || undefined,
        is_active: data.is_active,
        // Agent configuration fields are not included - managed via webhook/sync
      };

      await updateLocationMutation.mutateAsync({
        id: location.id,
        data: updateData,
      });

      toaster.create({
        title: 'Location Updated',
        description: 'Location has been updated successfully',
        type: 'success',
      });

      onSuccess?.();
    } catch (error) {
      console.error('Failed to update location:', error);
      toaster.create({
        title: 'Update Failed',
        description: 'Failed to update location. Please try again.',
        type: 'error',
      });
    }
  };

  const getStatusColor = (isActive: boolean): string => {
    return isActive ? 'blue' : 'gray'; // Changed from green to blue to match AdminPage
  };

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={6} align="stretch">
        {/* Header Section */}
        <Box>
          <HStack gap={4} mb={4}>
            <Avatar.Root size="md" bg="blue.500" color="white">
              <Avatar.Fallback>
                <FaMapMarkerAlt size={20} />
              </Avatar.Fallback>
            </Avatar.Root>
            <Stack direction="column" align="start" gap={1}>
              <Text fontSize="lg" fontWeight="semibold">
                {location.name}
              </Text>
              <HStack>
                <Badge
                  colorScheme={getStatusColor(location.is_active || false)}
                  variant="subtle"
                  size="sm"
                >
                  {location.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </HStack>
            </Stack>
          </HStack>

          <HStack gap={6} fontSize="sm" color="gray.600">
            {location.created_at && (
              <Text>
                Created: {format(new Date(location.created_at), 'MMM dd, yyyy')}
              </Text>
            )}
            {location.updated_at && (
              <Text>
                Updated: {format(new Date(location.updated_at), 'MMM dd, yyyy')}
              </Text>
            )}
          </HStack>
        </Box>

        <Separator />

        {/* Location Information */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
            Location Information
          </Text>
          <Stack gap={4}>
            <Field
              label="Location Name"
              required
              invalid={!!errors.name}
              errorText={errors.name?.message}
            >
              <Input
                {...register('name')}
                disabled={isViewMode}
                placeholder="Enter location name"
                borderRadius="md"
              />
            </Field>

            <Field
              label="Address"
              invalid={!!errors.address}
              errorText={errors.address?.message}
            >
              <Input
                {...register('address')}
                disabled={isViewMode}
                placeholder="Enter location address"
                borderRadius="md"
              />
            </Field>

            <HStack gap={4}>
              <Field
                label="Phone"
                invalid={!!errors.phone}
                errorText={errors.phone?.message}
                flex={1}
              >
                <Input
                  {...register('phone')}
                  disabled={isViewMode}
                  placeholder="Enter phone number"
                  borderRadius="md"
                />
              </Field>

              <Field
                label="Email"
                invalid={!!errors.email}
                errorText={errors.email?.message}
                flex={1}
              >
                <Input
                  {...register('email')}
                  disabled={isViewMode}
                  placeholder="Enter email address"
                  type="email"
                  borderRadius="md"
                />
              </Field>
            </HStack>

            <Field
              label="Website URL"
              invalid={!!errors.url}
              errorText={errors.url?.message}
            >
              <Input
                {...register('url')}
                disabled={isViewMode}
                placeholder="Enter website URL"
                type="url"
                borderRadius="md"
              />
            </Field>
          </Stack>
        </Box>

        <Separator />

        {/* Location Details */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
            Location Details
          </Text>
          <Stack gap={4}>
            <HStack gap={4}>
              <Field
                label="Postal Code"
                invalid={!!errors.postal_code}
                errorText={errors.postal_code?.message}
                flex={1}
              >
                <Input
                  {...register('postal_code')}
                  disabled={isViewMode}
                  placeholder="Enter postal code"
                  borderRadius="md"
                />
              </Field>

              <Field
                label="Country Code"
                invalid={!!errors.country_code}
                errorText={errors.country_code?.message}
                flex={1}
              >
                <Input
                  {...register('country_code')}
                  disabled={isViewMode}
                  placeholder="Enter country code (e.g., US)"
                  borderRadius="md"
                />
              </Field>
            </HStack>

            <Field
              label="Language"
              invalid={!!errors.language_code}
              errorText={errors.language_code?.message}
            >
              <Controller
                name="language_code"
                control={control}
                render={({ field }) => (
                  <Select.Root
                    collection={languageCollection}
                    size="sm"
                    width="100%"
                    positioning={{ sameWidth: true, strategy: 'fixed' }}
                    value={field.value ? [field.value] : []}
                    onValueChange={(e) => {
                      field.onChange(e.value[0] || '');
                    }}
                    disabled={isViewMode}
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="Select language" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {languageCollection.items.map((lang) => (
                          <Select.Item key={lang.value} item={lang}>
                            {lang.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                )}
              />
            </Field>
          </Stack>
        </Box>

        <Separator />

        {/* Agent Configuration */}
        <Box>
          <HStack justify="space-between" align="center" mb={2}>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              Agent Configuration
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSyncAgentSettings}
              loading={isSyncing}
              disabled={isSyncing || isViewMode}
            >
              <FaSync />
              Sync from ClubPlanner
            </Button>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={4}>
            These settings are managed via ClubPlanner webhook. Use the sync
            button to manually refresh.
          </Text>
          <Stack gap={4}>
            <HStack gap={4}>
              <Field label="Pay Per Use" flex={1}>
                <Input
                  value={location.pay_per_use ? 'Yes' : 'No'}
                  disabled
                  borderRadius="md"
                  bg="gray.50"
                />
              </Field>
              <Field label="Max Interactions Per Month" flex={1}>
                <Input
                  value={location.max_interaction_per_month ?? 'Not set'}
                  disabled
                  borderRadius="md"
                  bg="gray.50"
                />
              </Field>
              <Field label="Max Interactions Per Session" flex={1}>
                <Input
                  value={location.max_interaction_per_session ?? 'Not set'}
                  disabled
                  borderRadius="md"
                  bg="gray.50"
                />
              </Field>
            </HStack>
            <HStack gap={4}>
              <Field label="Agent Default Active" flex={1}>
                <Input
                  value={location.agent_default_active ? 'Yes' : 'No'}
                  disabled
                  borderRadius="md"
                  bg="gray.50"
                />
              </Field>
              <Field label="Agent Delay" flex={1}>
                <Input
                  value={
                    location.agent_delay
                      ? `${location.agent_delay_time || 0}s`
                      : 'Disabled'
                  }
                  disabled
                  borderRadius="md"
                  bg="gray.50"
                />
              </Field>
              <Field label="Agent Delay After Employee" flex={1}>
                <Input
                  value={
                    location.agent_delay_after_employee
                      ? `${location.agent_delay_after_employee_time || 0}s`
                      : 'Disabled'
                  }
                  disabled
                  borderRadius="md"
                  bg="gray.50"
                />
              </Field>
            </HStack>
          </Stack>
        </Box>

        <Separator />

        {/* Status */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
            Status
          </Text>
          <Field label="Active Status">
            <HStack>
              <Controller
                control={control}
                name="is_active"
                render={({ field }) => (
                  <Switch
                    name="is_active"
                    checked={field.value}
                    onCheckedChange={(details) => {
                      field.onChange(details.checked);
                    }}
                    disabled={isViewMode}
                  />
                )}
              />
              <Text fontSize="sm" color="gray.600">
                Location is {watch('is_active') ? 'active' : 'inactive'}
              </Text>
            </HStack>
          </Field>
        </Box>
      </Stack>
    </Box>
  );
});

LocationEditForm.displayName = 'LocationEditForm';
